import {useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Hint} from '@/components/ui/hint'
import {TrashGlyph} from '@/components/ui/icons'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {Switch} from '@/components/ui/switch'
import {Textarea} from '@/components/ui/textarea'
import {HelpIcon} from '@/components/help-icon'
import {
  subirArchivoDeFeedback,
  useEnviarFeedback,
  useResumenDeSesion,
  type ArchivoRegistro,
  type Captura,
  type FeedbackReporteWrite,
} from '@/hooks/producto/feedback/use-feedback'
import {publicarExito} from '@/lib/avisos'
import {MIN_PASOS_RESUMEN, leerBitacora} from '@/lib/bitacora-sesion'
import {translateApiError} from '@/lib/errors'
import {contextoDeTelemetria} from '@/lib/telemetry'
import {cn} from '@/lib/utils'

type Modo = 'acaba_de_pasar' | 'general'
type Tipo = FeedbackReporteWrite['tipo']
type Ambito = FeedbackReporteWrite['ambito']

// Mismos formatos y tope que la evidencia: se verifican antes de gastar un mint y el servidor repite.
const ACEPTADOS = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
])
const MAX_BYTES = 20 * 1024 * 1024
const ACCEPT = 'image/jpeg,image/png,image/webp,.pdf,.xlsx,.docx'

function Chips<T extends string>({
  label,
  valor,
  opciones,
  onChange,
  testId,
}: {
  label: string
  valor: T
  opciones: ReadonlyArray<readonly [T, string]>
  onChange: (valor: T) => void
  testId: string
}) {
  return (
    <fieldset className="m-0 min-w-0 space-y-1.5 border-0 p-0">
      <legend className="mb-1.5 text-sm leading-none font-semibold" data-testid={`${testId}-label`}>
        {label}
      </legend>
      <div className="flex gap-1.5">
        {opciones.map(([clave, rotulo]) => (
          <Button
            key={clave}
            type="button"
            aria-pressed={valor === clave}
            data-testid={`${testId}-${clave}`}
            size="sm"
            variant={valor === clave ? 'default' : 'outline'}
            onClick={() => onChange(clave)}>
            {rotulo}
          </Button>
        ))}
      </div>
    </fieldset>
  )
}

/** Sin formulario HTML a propósito: el único obligatorio es el texto y lo decide el botón. El resumen se
 * pide aparte y solo con bitácora suficiente (KAI-8). */
export function FeedbackDialog({
  open,
  onOpenChange,
  businessId,
  ruta,
  captura,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  ruta: string
  captura: Captura | null
}) {
  const {t} = useTranslation()
  const [nodo, setNodo] = useState<HTMLElement | null>(null)
  const [modo, setModo] = useState<Modo>('acaba_de_pasar')
  // Tipo y ámbito fijan las etiquetas del reporte en Plane.
  const [tipo, setTipo] = useState<Tipo>('bug')
  const [ambito, setAmbito] = useState<Ambito>('funcional')
  const [texto, setTexto] = useState('')
  const [resumenEditado, setResumenEditado] = useState<string | null>(null)
  const [resumenPedido, setResumenPedido] = useState(false)
  const [incluirCaptura, setIncluirCaptura] = useState(false)
  // Oculta hasta el `onLoad` y fuera si falla (CSP, PNG vacío): nunca un `<img>` roto a la vista.
  const [estadoCaptura, setEstadoCaptura] = useState<'cargando' | 'lista' | 'fallida'>('cargando')
  const [adjuntos, setAdjuntos] = useState<File[]>([])
  const [errorArchivo, setErrorArchivo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // La bitácora se congela al abrir: lo que pasó ANTES del reporte es el contexto.
  const bitacora = useMemo(() => leerBitacora(), [])

  const puedeResumir = modo === 'acaba_de_pasar' && bitacora.length >= MIN_PASOS_RESUMEN
  const resumen = useResumenDeSesion(businessId, bitacora, puedeResumir && resumenPedido)
  const enviar = useEnviarFeedback(businessId)
  const resumenVisible = resumenEditado ?? resumen.data?.resumen ?? ''
  const puedeEnviar = texto.trim().length > 0 && !enviando

  const agregarAdjuntos = (lista: FileList | null) => {
    if (!lista) return
    const nuevos = Array.from(lista)
    const rechazado = nuevos.find((f) => !ACEPTADOS.has(f.type) || f.size > MAX_BYTES)
    setErrorArchivo(rechazado ? t('feedback.errors.formato') : null)
    setAdjuntos((actuales) => [...actuales, ...nuevos.filter((f) => ACEPTADOS.has(f.type) && f.size <= MAX_BYTES)])
    if (inputRef.current) inputRef.current.value = ''
  }

  const onEnviar = async () => {
    setEnviando(true)
    setError(null)
    try {
      const archivos: ArchivoRegistro[] = []
      if (incluirCaptura && captura && estadoCaptura === 'lista') {
        archivos.push(
          await subirArchivoDeFeedback(
            businessId,
            new File([captura.blob], 'captura.png', {type: 'image/png'}),
            'captura'
          )
        )
      }
      for (const archivo of adjuntos) archivos.push(await subirArchivoDeFeedback(businessId, archivo, 'adjunto'))
      const telemetria = contextoDeTelemetria()
      await enviar.mutateAsync({
        modo,
        tipo,
        ambito,
        texto: texto.trim(),
        ruta,
        resumen: puedeResumir && resumenPedido && resumenVisible.trim() ? resumenVisible.trim() : null,
        resumen_sugerido: puedeResumir && resumenPedido ? (resumen.data?.resumen ?? null) : null,
        frontend_release: import.meta.env.VITE_SENTRY_RELEASE || null,
        posthog_session_id: telemetria.posthogSessionId,
        replay_url: telemetria.replayUrl,
        sentry_event_id: telemetria.sentryEventId,
        bitacora: modo === 'acaba_de_pasar' ? bitacora : [],
        archivos,
      })
      publicarExito(t('feedback.enviado.titulo'), t('feedback.enviado.detalle'))
      onOpenChange(false)
    } catch (e) {
      setError(translateApiError(e))
      setEnviando(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={setNodo} data-testid="feedback-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{t('feedback.titulo')}</DialogTitle>
        </DialogHeader>

        <Chips
          label={t('feedback.modo.label')}
          valor={modo}
          opciones={[
            ['acaba_de_pasar', t('feedback.modo.acabaDePasar')],
            ['general', t('feedback.modo.general')],
          ]}
          onChange={setModo}
          testId="feedback-modo"
        />
        <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
          <Chips
            label={t('feedback.tipo.label')}
            valor={tipo}
            opciones={[
              ['bug', t('feedback.tipo.bug')],
              ['mejora', t('feedback.tipo.mejora')],
            ]}
            onChange={setTipo}
            testId="feedback-tipo"
          />
          <Chips
            label={t('feedback.ambito.label')}
            valor={ambito}
            opciones={[
              ['visual', t('feedback.ambito.visual')],
              ['funcional', t('feedback.ambito.funcional')],
            ]}
            onChange={setAmbito}
            testId="feedback-ambito"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="feedback-texto">{t('feedback.texto')}</Label>
          <Textarea
            id="feedback-texto"
            data-testid="feedback-texto"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            minRows={3}
            maxLength={4000}
          />
        </div>

        {puedeResumir && !resumenPedido && (
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="feedback-resumir"
              onClick={() => setResumenPedido(true)}>
              {t('feedback.resumir')}
            </Button>
          </div>
        )}

        {puedeResumir && resumenPedido && (resumen.isPending || resumen.data?.resumen) && (
          <div className="space-y-1.5">
            <Label htmlFor="feedback-resumen">
              {t('feedback.resumen')}
              <HelpIcon label={t('common.help.iconLabelFor', {campo: t('feedback.resumen')})} contenedor={nodo}>
                {t('feedback.resumenAyuda')}
              </HelpIcon>
            </Label>
            {resumen.isPending ? (
              <Skeleton data-testid="feedback-resumen-cargando" className="h-16 rounded-lg" />
            ) : (
              <Textarea
                id="feedback-resumen"
                data-testid="feedback-resumen"
                value={resumenVisible}
                onChange={(e) => setResumenEditado(e.target.value)}
                minRows={3}
                maxRows={12}
                maxLength={2000}
              />
            )}
            <Hint testId="feedback-resumen-hint">{t('feedback.resumenHint')}</Hint>
          </div>
        )}

        {puedeResumir && resumenPedido && resumen.isError && (
          <p role="alert" data-testid="feedback-resumen-error" className="text-sm text-destructive">
            {translateApiError(resumen.error)}
          </p>
        )}

        {captura && estadoCaptura !== 'fallida' && (
          <div
            data-testid="feedback-captura-bloque"
            hidden={estadoCaptura !== 'lista'}
            className="flex items-center gap-3">
            <img
              src={captura.url}
              alt={t('feedback.capturaAlt')}
              data-testid="feedback-captura"
              onLoad={() => setEstadoCaptura('lista')}
              onError={() => setEstadoCaptura('fallida')}
              className={cn(
                'h-14 w-20 rounded-md border border-border object-cover object-top',
                !incluirCaptura && 'opacity-50'
              )}
            />
            <Label htmlFor="feedback-captura-switch" className="cursor-pointer">
              <Switch
                id="feedback-captura-switch"
                data-testid="feedback-captura-switch"
                checked={incluirCaptura}
                onCheckedChange={setIncluirCaptura}
              />
              {t('feedback.capturaIncluir')}
              <HelpIcon label={t('common.help.iconLabelFor', {campo: t('feedback.capturaIncluir')})} contenedor={nodo}>
                {t('feedback.capturaAyuda')}
              </HelpIcon>
            </Label>
          </div>
        )}

        <div className="space-y-1.5">
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            data-testid="feedback-adjuntos-input"
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => agregarAdjuntos(e.target.files)}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="feedback-adjuntar"
            onClick={() => inputRef.current?.click()}>
            {t('feedback.adjuntar')}
          </Button>
          {adjuntos.length > 0 && (
            <ul data-testid="feedback-adjuntos" className="space-y-1 text-sm">
              {adjuntos.map((archivo, i) => (
                <li key={`${archivo.name}-${i}`} className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate">{archivo.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label={t('feedback.quitarAdjunto', {nombre: archivo.name})}
                    onClick={() => setAdjuntos((actuales) => actuales.filter((_, j) => j !== i))}>
                    <TrashGlyph />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {errorArchivo && (
            <p role="alert" data-testid="feedback-error-archivo" className="text-sm text-destructive">
              {errorArchivo}
            </p>
          )}
        </div>

        {error && (
          <p role="alert" data-testid="feedback-error" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" data-testid="feedback-enviar" disabled={!puedeEnviar} onClick={onEnviar}>
            {enviando ? t('feedback.enviando') : t('feedback.enviar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
